import {
    Component,
    EventEmitter,
    Input,
    OnChanges,
    OnInit,
    Output,
    SimpleChanges
} from '@angular/core';
import {
    addMonths,
    addYears,
    endOfMonth,
    endOfYear,
    format,
    getMonth,
    startOfMonth,
    startOfYear,
    subMonths,
    subYears
} from 'date-fns';
import { DatePickerType } from '../constants/date-picker-type';
import { IDateRange } from '../models/date-range';

@Component({
    selector: 'calendar',
    template: `
        <div *ngIf="!isDualCalendar" class="calendar-header" role="navigation">
            <ng-container *ngIf="type === datePickerType.Single || type === datePickerType.Range">
                <div class="btn-left-container">
                    <button (click)="goToPreviousMonth()">
                        <svg-icon *ngIf="!!leftArrowSvgSrc" [src]="leftArrowSvgSrc"> </svg-icon>
                        <span *ngIf="!leftArrowSvgSrc">←</span>
                    </button>
                </div>
                <div class="month-heading-container">
                    <h1 class="month-heading">
                        <span>{{ format(month, 'MMMM YYYY') }}</span>
                    </h1>
                </div>
                <div
                    class="btn-right-container"
                    [ngClass]="{ 'btn-container-disabled': !nextMonthIsAvailable() }"
                >
                    <button [disabled]="!nextMonthIsAvailable()" (click)="goToNextMonth()">
                        <svg-icon *ngIf="!!rightArrowSvgSrc" [src]="rightArrowSvgSrc"> </svg-icon>
                        <span *ngIf="!rightArrowSvgSrc">→</span>
                    </button>
                </div>
            </ng-container>

            <ng-container
                *ngIf="type === datePickerType.Month || type === datePickerType.MonthRange"
            >
                <div class="btn-left-container">
                    <button (click)="goToPreviousYear()">
                        <svg-icon *ngIf="!!leftArrowSvgSrc" [src]="leftArrowSvgSrc"> </svg-icon>
                        <span *ngIf="!leftArrowSvgSrc">←</span>
                    </button>
                </div>
                <div class="year-heading-container">
                    <h1 class="year-heading">
                        <span>{{ format(year, 'YYYY') }}</span>
                    </h1>
                </div>
                <div
                    class="btn-right-container"
                    [ngClass]="{ 'btn-container-disabled': !nextYearIsAvailable() }"
                >
                    <button [disabled]="!nextYearIsAvailable()" (click)="goToNextYear()">
                        <svg-icon *ngIf="!!rightArrowSvgSrc" [src]="rightArrowSvgSrc"> </svg-icon>
                        <span *ngIf="!rightArrowSvgSrc">→</span>
                    </button>
                </div>
            </ng-container>
        </div>

        <div *ngIf="isDualCalendar" class="calendar-header calendar-header-heading-only">
            <div class="month-heading-container">
                <h1 class="month-heading">
                    <span *ngIf="type === datePickerType.Single || type === datePickerType.Range">{{
                        format(month, 'MMMM YYYY')
                    }}</span>
                    <span
                        *ngIf="type === datePickerType.Month || type === datePickerType.MonthRange"
                        >{{ format(year, 'YYYY') }}</span
                    >
                </h1>
            </div>
        </div>

        <ng-container *ngIf="type === datePickerType.Single || type === datePickerType.Range">
            <calendar-days-grid
                [month]="month"
                [date]="type === datePickerType.Single ? date : null"
                [dateRange]="type === datePickerType.Range ? dateRange : null"
                [rangeHovered]="rangeHovered"
                [hidePreviousMonth]="isDualCalendarEnd"
                [hideNextMonth]="isDualCalendarStart"
                [lowerLimit]="lowerLimit"
                [upperLimit]="upperLimit"
                [isSettingDateRangeStart]="isSettingDateRangeStart"
                [isSettingDateRangeEnd]="isSettingDateRangeEnd"
                (dateChange)="handleDateChange($event)"
                (dateHoveredChange)="handleDateHoveredChange($event)"
            >
            </calendar-days-grid>
        </ng-container>

        <ng-container *ngIf="type === datePickerType.Month || type === datePickerType.MonthRange">
            <calendar-months-grid
                [year]="year"
                [date]="type === datePickerType.Month ? date : null"
                [dateRange]="type === datePickerType.MonthRange ? dateRange : null"
                [lowerLimit]="lowerLimit"
                [upperLimit]="upperLimit"
                (dateChange)="handleDateChange($event)"
            >
            </calendar-months-grid>
        </ng-container>
    `,
    styleUrls: ['./calendar.component.scss']
})
export class CalendarComponent implements OnChanges, OnInit {
    @Input() public date: Date;
    @Input() public dateRange: IDateRange;
    @Input() public type: DatePickerType;
    @Input() public lowerLimit: Date;
    @Input() public upperLimit: Date;
    @Input() public allowFuture = false;
    @Input() public isDualCalendarStart: boolean;
    @Input() public isDualCalendarEnd: boolean;
    @Input() public isSettingDateRangeStart: boolean;
    @Input() public isSettingDateRangeEnd: boolean;
    @Input() public rangeHovered: IDateRange;
    @Input() public leftArrowSvgSrc: string;
    @Input() public rightArrowSvgSrc: string;
    @Output() public dateChange = new EventEmitter<Date>();
    @Output() public dateRangeChange = new EventEmitter<IDateRange>();
    @Output() public rangeHoveredChange = new EventEmitter<IDateRange>();
    @Output() public dayClick = new EventEmitter<Date>();

    public format = format;
    public datePickerType = DatePickerType;
    private _month: Date;
    private _year: Date;
    private _now = new Date();

    // Lifecycle hooks.
    public ngOnInit(): void {
        this._initYear();
    }

    public ngOnChanges(changes: SimpleChanges): void {
        if (this.type === DatePickerType.Single && !!changes.date) {
            this._syncMonthForSingleDate();
        }

        if (this.type === DatePickerType.Range && !!changes.dateRange) {
            this._syncMonthForDateRange();
        }
    }

    // Getters/setters.
    @Input()
    public set month(value: Date) {
        if (value) {
            this._month = startOfMonth(value);
        } else {
            this._month = null;
        }
    }
    public get month(): Date {
        return this._month;
    }

    @Input()
    public set year(value: Date) {
        if (value) {
            this._year = startOfYear(value);
        } else {
            this._year = null;
        }
    }
    public get year(): Date {
        return this._year;
    }

    public get isDualCalendar(): boolean {
        return this.isDualCalendarStart || this.isDualCalendarEnd;
    }

    // Event handlers.
    public handleDateChange(value: Date): void {
        if (this.type === DatePickerType.Single || this.type === DatePickerType.Month) {
            const newDate: Date = value;
            this.month = value;
            this.date = newDate;
            this.dateChange.emit(this.date);
        } else if (this.type === DatePickerType.Range || this.type === DatePickerType.MonthRange) {
            const newRange: IDateRange = { ...this.dateRange };
            const isSettingDateRangeEnd = this.isSettingDateRangeEnd && value >= newRange.start;
            const isSettingDateRangeStart = !isSettingDateRangeEnd;

            if (isSettingDateRangeStart) {
                newRange.start = value;
                newRange.end = null;
            } else {
                newRange.end = value;
            }

            // If attempting to select a month range, set `range.end` to the end of the month.
            // If the end of the month is past `upperLimit`, set it to `upperLimit` instead.
            if (!!newRange.end && this.type === DatePickerType.MonthRange) {
                let newRangeEnd = endOfMonth(newRange.end);
                if (!!this.upperLimit && this.upperLimit < newRangeEnd) {
                    newRangeEnd = this.upperLimit;
                }
                newRange.end = newRangeEnd;
            }

            // Reset the range hover model.
            this.rangeHoveredChange.emit({
                start: newRange.start,
                end: newRange.end || newRange.start
            });

            this.dateRange = newRange;
            this.dateRangeChange.emit(this.dateRange);
        }

        this.dayClick.emit(value);
    }

    public handleDateHoveredChange(value: Date): void {
        if (this.type === DatePickerType.Range) {
            const model = this.dateRange;
            if (this.isSettingDateRangeEnd && !!model && !!(model as IDateRange).start) {
                if (value >= model.start) {
                    this.rangeHoveredChange.emit({
                        start: model.start,
                        end: value
                    });
                } else {
                    this.rangeHoveredChange.emit({
                        start: model.start,
                        end: model.start
                    });
                }
            }
        }
    }

    // Navigation.
    public goToNextMonth(): void {
        this.month = addMonths(this.month, 1);
    }

    public goToPreviousMonth(): void {
        this.month = subMonths(this.month, 1);
    }

    public nextMonthIsAvailable(): boolean {
        return !this.upperLimit || this.upperLimit > endOfMonth(this.month);
    }

    public goToNextYear(): void {
        this.year = addYears(this.year, 1);
    }

    public goToPreviousYear(): void {
        this.year = subYears(this.year, 1);
    }

    public nextYearIsAvailable(): boolean {
        return !this.upperLimit || this.upperLimit > endOfYear(this.year);
    }

    // Helpers.
    private _initYear(): void {
        if (this.type === DatePickerType.Month || this.type === DatePickerType.MonthRange) {
            this.year = this.date || this._now;
        }
    }

    private _syncMonthForSingleDate(): void {
        this.month = startOfMonth(this.date || this._now);
    }

    private _syncMonthForDateRange(): void {
        if (!this.dateRange?.start && !this.dateRange?.end) {
            if (this.isDualCalendarStart) {
                this.month = subMonths(this._now, 1);
            }
            if (this.isDualCalendarEnd) {
                this.month = this._now;
            }
        } else if (this.dateRange?.start && this.dateRange?.end) {
            const rangeCrossesMonths =
                getMonth(this.dateRange.start) !== getMonth(this.dateRange.end);
            const rangeEndsAtCurrentMonth = getMonth(this._now) === getMonth(this.dateRange.end);

            if (rangeCrossesMonths) {
                if (this.isDualCalendarStart) {
                    this.month = this.dateRange.start;
                }
                if (this.isDualCalendarEnd) {
                    this.month = this.dateRange.end;
                }
            } else if (rangeEndsAtCurrentMonth) {
                if (this.isDualCalendarStart) {
                    this.month = subMonths(this.dateRange.end, 1);
                }
                if (this.isDualCalendarEnd) {
                    this.month = this.dateRange.end;
                }
            }
        }
    }
}
